import { useEffect, useState } from 'react';
import { subscribeRoute } from './router.js';

/**
 * THE AREA-CHIP SHEET'S OPEN/CLOSE STATE (nav redesign 10b) — a module store, same idiom as
 * `router.ts` and `i18n.ts`, for one reason: the two consumers sit far apart in the tree. The
 * `AppNav` sheet renders in the shell; the `AreaChip` that toggles it renders deep inside a
 * routed screen, past the auth gate, the seat gate and the detail-vs-list branch. A Context
 * provider would have to wrap both and survive every early return in `app.tsx`; a module store
 * sidesteps that and is unit-testable as a pure module the way `parseRoute` is.
 *
 * ⚠️ CLOSE-ON-NAVIGATION LIVES HERE, EXACTLY ONCE. Every `navigate()` and every browser Back
 * fires the route subscription, so subscribing to it at module load is the single place the
 * sheet is closed when the destination changes — no screen and no chip has to remember to.
 * (`subscribeRoute` fires immediately too; `open` is already false, and the idempotent guard
 * below makes that first call a no-op.)
 */
let open = false;
const listeners = new Set<(open: boolean) => void>();

export function isNavSheetOpen(): boolean {
  return open;
}

export function setNavSheetOpen(next: boolean): void {
  // Guard equal writes: a redundant notify would re-render every subscriber for nothing, and
  // the route subscription fires setNavSheetOpen(false) on every navigation whether or not the
  // sheet was open.
  if (open === next) return;
  open = next;
  for (const listener of listeners) listener(open);
}

export function toggleNavSheet(): void {
  setNavSheetOpen(!open);
}

export function subscribeNavSheet(listener: (open: boolean) => void): () => void {
  listeners.add(listener);
  listener(open);
  return () => {
    listeners.delete(listener);
  };
}

// The one close-on-navigation path. Set up at module load, never torn down — the store lives
// as long as the app does.
subscribeRoute(() => setNavSheetOpen(false));

/** Re-render on sheet open/close. Same shape as `useLanguage`: subscribe, fire immediately,
 * unsubscribe on unmount. */
export function useNavSheet(): boolean {
  const [current, setCurrent] = useState<boolean>(() => open);
  useEffect(() => subscribeNavSheet(setCurrent), []);
  return current;
}
